/**
 * Theme management utilities
 * Handles light/dark mode with cappuccino and tobacco paper colors
 */

export type Theme = 'light' | 'dark'

const THEME_STORAGE_KEY = 'tlahtolamoxtli-theme'

/**
 * Get the current theme from localStorage or system preference
 */
export function getTheme(): Theme {
  const stored = localStorage.getItem(THEME_STORAGE_KEY) as Theme | null
  if (stored === 'light' || stored === 'dark') {
    return stored
  }
  // Default to system preference
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

/**
 * Set the theme and update the document
 */
export function setTheme(theme: Theme) {
  localStorage.setItem(THEME_STORAGE_KEY, theme)
  document.documentElement.setAttribute('data-theme', theme)
  applyThemeColors(theme)
}

/**
 * Apply theme colors to CSS variables
 */
export function applyThemeColors(theme: Theme) {
  const root = document.documentElement
  
  if (theme === 'light') {
    // Cappuccino paper colors
    root.style.setProperty('--bg-primary', '#F5E6D3')
    root.style.setProperty('--bg-secondary', '#E8D5C4')
    root.style.setProperty('--text-primary', '#3E2723')
    root.style.setProperty('--text-secondary', '#5D4037')
    root.style.setProperty('--border-color', '#D7CCC8')
    root.style.setProperty('--card-bg', '#F5E6D3')
    root.style.setProperty('--shadow', 'rgba(0, 0, 0, 0.1)')
    
    // Material Web Components theme tokens - Light mode
    root.style.setProperty('color-scheme', 'light')
    root.style.setProperty('--md-sys-color-surface', '#F5E6D3')
    root.style.setProperty('--md-sys-color-surface-container', '#F5E6D3')
    root.style.setProperty('--md-sys-color-surface-container-high', '#F5E6D3')
    root.style.setProperty('--md-sys-color-surface-container-highest', '#F5E6D3')
    root.style.setProperty('--md-sys-color-on-surface', '#3E2723')
    root.style.setProperty('--md-sys-color-on-surface-variant', '#5D4037')
    root.style.setProperty('--md-sys-color-primary', '#5D4037')
    root.style.setProperty('--md-sys-color-on-primary', '#EFEBE9')
    root.style.setProperty('--md-sys-color-outline', '#D7CCC8')
    root.style.setProperty('--md-sys-color-outline-variant', '#E8D5C4')
    
    // MWC Text Field tokens
    root.style.setProperty('--md-filled-text-field-container-color', '#F5E6D3')
    root.style.setProperty('--md-filled-text-field-input-text-color', '#3E2723')
    root.style.setProperty('--md-filled-text-field-label-text-color', '#5D4037')
    root.style.setProperty('--md-filled-text-field-supporting-text-color', '#5D4037')
    root.style.setProperty('--md-filled-text-field-input-text-placeholder-color', '#5D4037')
    
    // MWC Select tokens
    root.style.setProperty('--md-filled-select-container-color', '#F5E6D3')
    root.style.setProperty('--md-filled-select-label-text-color', '#5D4037')
    root.style.setProperty('--md-filled-select-input-text-color', '#3E2723')
    
    // MWC Menu tokens (for select dropdown)
    root.style.setProperty('--md-menu-container-color', '#F5E6D3')
    root.style.setProperty('--md-menu-container-shape', '4px')
    root.style.setProperty('--md-menu-item-label-text-color', '#3E2723')
    root.style.setProperty('--md-menu-item-hover-state-layer-color', 'rgba(61, 39, 35, 0.08)')
    root.style.setProperty('--md-menu-item-focus-state-layer-color', 'rgba(61, 39, 35, 0.12)')
    root.style.setProperty('--md-menu-item-selected-container-color', '#3E2723')
    root.style.setProperty('--md-menu-item-selected-label-text-color', '#EFEBE9')
    
    // MWC Outlined Button tokens
    root.style.setProperty('--md-outlined-button-container-color', 'transparent')
    root.style.setProperty('--md-outlined-button-label-text-color', '#3E2723')
    root.style.setProperty('--md-outlined-button-outline-color', '#D7CCC8')
    
    // MWC Filled Button tokens
    root.style.setProperty('--md-filled-button-container-color', '#5D4037')
    root.style.setProperty('--md-filled-button-label-text-color', '#EFEBE9')
  } else {
    // Tobacco paper colors
    root.style.setProperty('--bg-primary', '#3E2723')
    root.style.setProperty('--bg-secondary', '#4E342E')
    root.style.setProperty('--text-primary', '#EFEBE9')
    root.style.setProperty('--text-secondary', '#D7CCC8')
    root.style.setProperty('--border-color', '#5D4037')
    root.style.setProperty('--card-bg', '#4E342E')
    root.style.setProperty('--shadow', 'rgba(0, 0, 0, 0.3)')
    
    // Material Web Components theme tokens - Dark mode
    root.style.setProperty('color-scheme', 'dark')
    root.style.setProperty('--md-sys-color-surface', '#3E2723')
    root.style.setProperty('--md-sys-color-surface-container', '#4E342E')
    root.style.setProperty('--md-sys-color-surface-container-high', '#5D4037')
    root.style.setProperty('--md-sys-color-surface-container-highest', '#5D4037')
    root.style.setProperty('--md-sys-color-on-surface', '#EFEBE9')
    root.style.setProperty('--md-sys-color-on-surface-variant', '#D7CCC8')
    root.style.setProperty('--md-sys-color-primary', '#D7CCC8')
    root.style.setProperty('--md-sys-color-on-primary', '#3E2723')
    root.style.setProperty('--md-sys-color-outline', '#5D4037')
    root.style.setProperty('--md-sys-color-outline-variant', '#4E342E')
    
    // MWC Text Field tokens
    root.style.setProperty('--md-filled-text-field-container-color', '#4E342E')
    root.style.setProperty('--md-filled-text-field-input-text-color', '#EFEBE9')
    root.style.setProperty('--md-filled-text-field-label-text-color', '#D7CCC8')
    root.style.setProperty('--md-filled-text-field-supporting-text-color', '#D7CCC8')
    root.style.setProperty('--md-filled-text-field-input-text-placeholder-color', '#D7CCC8')
    
    // MWC Select tokens
    root.style.setProperty('--md-filled-select-container-color', '#4E342E')
    root.style.setProperty('--md-filled-select-label-text-color', '#D7CCC8')
    root.style.setProperty('--md-filled-select-input-text-color', '#EFEBE9')
    
    // MWC Menu tokens (for select dropdown)
    root.style.setProperty('--md-menu-container-color', '#4E342E')
    root.style.setProperty('--md-menu-container-shape', '4px')
    root.style.setProperty('--md-menu-item-label-text-color', '#EFEBE9')
    root.style.setProperty('--md-menu-item-hover-state-layer-color', 'rgba(239, 235, 233, 0.08)')
    root.style.setProperty('--md-menu-item-focus-state-layer-color', 'rgba(239, 235, 233, 0.12)')
    root.style.setProperty('--md-menu-item-selected-container-color', '#F5E6D3')
    root.style.setProperty('--md-menu-item-selected-label-text-color', '#3E2723')
    
    // MWC Outlined Button tokens
    root.style.setProperty('--md-outlined-button-container-color', 'transparent')
    root.style.setProperty('--md-outlined-button-label-text-color', '#EFEBE9')
    root.style.setProperty('--md-outlined-button-outline-color', '#5D4037')
    
    // MWC Filled Button tokens
    root.style.setProperty('--md-filled-button-container-color', '#D7CCC8')
    root.style.setProperty('--md-filled-button-label-text-color', '#3E2723')
  }
}

/**
 * Initialize theme on page load
 */
export function initTheme() {
  const theme = getTheme()
  setTheme(theme)
  
  // Listen for system theme changes
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
    if (!localStorage.getItem(THEME_STORAGE_KEY)) {
      setTheme(e.matches ? 'dark' : 'light')
    }
  })
}
